import { ScreenList } from '@/utils/hooks/useScreenSizes';
import { defaultXplorSpotLayout } from '@/utils/hooks/useXplorSpotLayout';
import { defaultXplorTraderLayout } from '@/utils/hooks/useXplorTraderLayout';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MosaicNode } from 'react-mosaic-component';
import { v4 as uuidv4 } from 'uuid';
import { RootState } from '../store';

export type OrderBookWorkspace = 'Vertical' | 'Horizontal';

export type XplorSpotWorkspace = {
    id: string;
    name: string;
    layout: {
        mobile: MosaicNode<string> | null;
        desktop?: MosaicNode<string> | null;
    };
    enlargedModule?: string;
};

export type XplorTraderWorkspace = {
    id: string;
    name: string;
    layout: {
        mobile: MosaicNode<string> | null;
        desktop?: MosaicNode<string> | null;
    };
    enlargedModule?: string;
};

export interface WorkspaceState {
    orderBook: OrderBookWorkspace;
    xplorSpotWorkspaceTabs: XplorSpotWorkspace[];
    selectedXplorSpotWorkspaceTab?: XplorSpotWorkspace;
    xplorTraderWorkspaceTabs: XplorTraderWorkspace[];
    selectedXplorTraderWorkspaceTab?: XplorTraderWorkspace;
    orderConfigs: { [key: string]: any };
    chartConfigs: { [key: string]: any };
    orderBookConfigs: { [key: string]: any };
}

export const defaultXplorSpotWorkspaceTabs = [
    {
        id: uuidv4(),
        name: 'XplorSpot',
        layout: defaultXplorSpotLayout(),
        enlargedModule: undefined
    }
];

export const defaultXplorTraderWorkspaceTabs = [
    {
        id: uuidv4(),
        name: 'XplorTrader',
        layout: defaultXplorTraderLayout(),
        enlargedModule: undefined
    }
];

export const initialWorkspaceState: WorkspaceState = {
    orderBook: 'Vertical',
    xplorSpotWorkspaceTabs: defaultXplorSpotWorkspaceTabs,
    selectedXplorSpotWorkspaceTab: defaultXplorSpotWorkspaceTabs[0],
    xplorTraderWorkspaceTabs: defaultXplorTraderWorkspaceTabs,
    selectedXplorTraderWorkspaceTab: defaultXplorTraderWorkspaceTabs[0],
    orderConfigs: {},
    chartConfigs: {},
    orderBookConfigs: {}
};

export const workspaceSlice = createSlice({
    name: 'workspace',
    initialState: initialWorkspaceState,
    reducers: {
        setOrderBookWorkspace: (state, action: PayloadAction<OrderBookWorkspace>) => {
            state.orderBook = action.payload;
        },
        setXplorSpotWorkspace: (state, action: PayloadAction<string>) => {
            const tab = state.xplorSpotWorkspaceTabs.find((tab) => tab.id === action.payload);
            if (tab) {
                state.selectedXplorSpotWorkspaceTab = tab;
            }
        },
        setXplorTraderWorkspace: (state, action: PayloadAction<string>) => {
            const tab = state.xplorTraderWorkspaceTabs.find((tab) => tab.id === action.payload);
            if (tab) {
                state.selectedXplorTraderWorkspaceTab = tab;
            }
        },
        setXplorSpotWorkspaceTabLayout: (
            state,
            action: PayloadAction<{
                id: string;
                screen: ScreenList;
                layout: MosaicNode<string> | null;
            }>
        ) => {
            const { id, screen, layout } = action.payload;

            let tabIndex = -1;

            if (id === 'current') {
                if (state.selectedXplorSpotWorkspaceTab) {
                    tabIndex = state.xplorSpotWorkspaceTabs.findIndex(
                        (tab) => tab.id === state.selectedXplorSpotWorkspaceTab?.id
                    );
                }
            } else {
                tabIndex = state.xplorSpotWorkspaceTabs.findIndex((tab) => tab.id === id);
            }

            if (tabIndex !== -1) {
                state.xplorSpotWorkspaceTabs[tabIndex].layout[screen] = layout;
                state.selectedXplorSpotWorkspaceTab = state.xplorSpotWorkspaceTabs[tabIndex];
            }
        },
        setXplorTraderWorkspaceTabLayout: (
            state,
            action: PayloadAction<{
                id: string;
                screen: ScreenList;
                layout: MosaicNode<string> | null;
            }>
        ) => {
            const { id, screen, layout } = action.payload;

            let tabIndex = -1;

            if (id === 'current') {
                if (state.selectedXplorTraderWorkspaceTab) {
                    tabIndex = state.xplorTraderWorkspaceTabs.findIndex(
                        (tab) => tab.id === state.selectedXplorTraderWorkspaceTab?.id
                    );
                }
            } else {
                tabIndex = state.xplorTraderWorkspaceTabs.findIndex((tab) => tab.id === id);
            }

            if (tabIndex !== -1) {
                state.xplorTraderWorkspaceTabs[tabIndex].layout[screen] = layout;
                state.selectedXplorTraderWorkspaceTab = state.xplorTraderWorkspaceTabs[tabIndex];
            }
        },
        setWorkspaceTabEnlargedModule: (
            state,
            action: PayloadAction<{
                workspaceType: 'xplorspot' | 'xplortrader';
                id?: string;
            }>
        ) => {
            const { workspaceType, id } = action.payload;
            if (workspaceType === 'xplorspot') {
                const tabIndex = state.xplorSpotWorkspaceTabs.findIndex(
                    (tab) => tab.id === state.selectedXplorSpotWorkspaceTab?.id
                );
                if (tabIndex !== -1) {
                    state.xplorSpotWorkspaceTabs[tabIndex].enlargedModule = id;
                    if (state.selectedXplorSpotWorkspaceTab) state.selectedXplorSpotWorkspaceTab.enlargedModule = id;
                }
            } else {
                const tabIndex = state.xplorTraderWorkspaceTabs.findIndex(
                    (tab) => tab.id === state.selectedXplorTraderWorkspaceTab?.id
                );
                if (tabIndex !== -1) {
                    state.xplorTraderWorkspaceTabs[tabIndex].enlargedModule = id;
                    if (state.selectedXplorTraderWorkspaceTab)
                        state.selectedXplorTraderWorkspaceTab.enlargedModule = id;
                }
            }
        },
        setOrderConfigs: (state, action: PayloadAction<{ key: string; config: any }>) => {
            const { key, config } = action.payload;
            if (state.orderConfigs) state.orderConfigs[key] = config;
            else state.orderConfigs = { [key]: config };
        },
        removeOrderConfig: (state, action: PayloadAction<{ key: string }>) => {
            const { key } = action.payload;
            if (state.orderConfigs && state.orderConfigs[key]) {
                delete state.orderConfigs[key];
            }
        },
        setChartConfigs: (state, action: PayloadAction<{ key: string; config: any }>) => {
            const { key, config } = action.payload;
            if (state.chartConfigs) state.chartConfigs[key] = config;
            else state.chartConfigs = { [key]: config };
        },
        removeChartConfig: (state, action: PayloadAction<{ key: string }>) => {
            const { key } = action.payload;
            if (state.chartConfigs && state.chartConfigs[key]) {
                delete state.chartConfigs[key];
            }
        },
        setOrderBookConfigs: (state, action: PayloadAction<{ key: string; config: any }>) => {
            const { key, config } = action.payload;
            if (state.orderBookConfigs) state.orderBookConfigs[key] = config;
            else state.orderBookConfigs = { [key]: config };
        },
        removeOrderBookConfig: (state, action: PayloadAction<{ key: string }>) => {
            const { key } = action.payload;
            if (state.orderBookConfigs && state.orderBookConfigs[key]) {
                delete state.orderBookConfigs[key];
            }
        },
        addXplorSpotWorkspaceTab: (state, action: PayloadAction<{ name: string }>) => {
            const { name } = action.payload;
            state.xplorSpotWorkspaceTabs.push({
                id: uuidv4(),
                name,
                layout: {
                    ...defaultXplorSpotLayout(),
                    desktop: undefined
                }
            });
            state.selectedXplorSpotWorkspaceTab = state.xplorSpotWorkspaceTabs[state.xplorSpotWorkspaceTabs.length - 1];
        },
        addXplorTraderWorkspaceTab: (state, action: PayloadAction<{ name: string }>) => {
            const { name } = action.payload;
            state.xplorTraderWorkspaceTabs.push({
                id: uuidv4(),
                name,
                layout: {
                    ...defaultXplorTraderLayout(),
                    desktop: undefined
                }
            });
            state.selectedXplorTraderWorkspaceTab =
                state.xplorTraderWorkspaceTabs[state.xplorTraderWorkspaceTabs.length - 1];
        },
        deleteXplorSpotWorkspaceTab: (state, action: PayloadAction<{ id: string }>) => {
            const { id } = action.payload;
            state.xplorSpotWorkspaceTabs = state.xplorSpotWorkspaceTabs.filter((tab) => tab.id !== id);
            if (state.selectedXplorSpotWorkspaceTab?.id === id) {
                state.selectedXplorSpotWorkspaceTab = state.xplorSpotWorkspaceTabs[0];
            }
        },
        deleteXplorTraderWorkspaceTab: (state, action: PayloadAction<{ id: string }>) => {
            const { id } = action.payload;
            state.xplorTraderWorkspaceTabs = state.xplorTraderWorkspaceTabs.filter((tab) => tab.id !== id);
            if (state.selectedXplorTraderWorkspaceTab?.id === id) {
                state.selectedXplorTraderWorkspaceTab = state.xplorTraderWorkspaceTabs[0];
            }
        },
        renameXplorSpotWorkspaceTab: (state, action: PayloadAction<{ id: string; name: string }>) => {
            const { id, name } = action.payload;
            const tab = state.xplorSpotWorkspaceTabs.find((tab) => tab.id === id);
            if (tab) {
                tab.name = name;
            }
        },
        renameXplorTraderWorkspaceTab: (state, action: PayloadAction<{ id: string; name: string }>) => {
            const { id, name } = action.payload;
            const tab = state.xplorTraderWorkspaceTabs.find((tab) => tab.id === id);
            if (tab) {
                tab.name = name;
            }
        }
    }
});

export const {
    setOrderBookWorkspace,
    setXplorSpotWorkspace,
    setXplorTraderWorkspace,
    setXplorSpotWorkspaceTabLayout,
    setXplorTraderWorkspaceTabLayout,
    setWorkspaceTabEnlargedModule,
    setOrderConfigs,
    removeOrderConfig,
    setChartConfigs,
    removeChartConfig,
    setOrderBookConfigs,
    removeOrderBookConfig,
    addXplorSpotWorkspaceTab,
    addXplorTraderWorkspaceTab,
    deleteXplorSpotWorkspaceTab,
    deleteXplorTraderWorkspaceTab,
    renameXplorSpotWorkspaceTab,
    renameXplorTraderWorkspaceTab
} = workspaceSlice.actions;

export const selectOrderBookWorkspace = (state: RootState) => state.workspace.orderBook;
export const selectXplorSpotWorkspaceTabs = (state: RootState) => state.workspace.xplorSpotWorkspaceTabs;
export const selectSelectedXplorSpotWorkspaceTab = (state: RootState) => state.workspace.selectedXplorSpotWorkspaceTab;
export const selectXplorTraderWorkspaceTabs = (state: RootState) => state.workspace.xplorTraderWorkspaceTabs;
export const selectSelectedXplorTraderWorkspaceTab = (state: RootState) =>
    state.workspace.selectedXplorTraderWorkspaceTab;
export const selectOrderConfigs = (state: RootState) => state.workspace.orderConfigs;
export const selectChartConfigs = (state: RootState) => state.workspace.chartConfigs;
export const selectOrderBookConfigs = (state: RootState) => state.workspace.orderBookConfigs;

export default workspaceSlice.reducer;
